/**
 * UpgradeModal — Fase 8.12
 *
 * Modal mostrato al tenant owner per attivare l'abbonamento dal trial.
 * Carica i piani VIVI da `/api/billing/plans` (SSOT plans_catalog.py),
 * mai hardcodati lato frontend. Al click su un piano fa una POST a
 * `/api/billing/checkout` e ridirige a Stripe Checkout — la stessa flow
 * usata dal pricing pubblico, così il provisioning passa per il webhook
 * canonico e mantiene 1 tenant = 1 customer = 1 subscription.
 *
 * Per i tenant già attivi (non in trial), questa modale può essere usata
 * per cambiare piano: la chiamata corretta è `/api/billing/subscription/
 * change-plan`. Il `state.kind` controlla quale endpoint usare.
 */
import React, { useEffect, useMemo, useState } from "react";
import { X, Sparkles, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";


// Tier ordering shown in the modal — derived from the catalog but normalized
// so we ALWAYS render Starter → Professional → Enterprise (sales narrative).
// Supports both restaurant tiers (starter/professional/enterprise) and the
// hotel tiers (hotel_starter / hotel_pro) + combo.
const TIER_ORDER = ["starter", "hotel_starter", "professional", "hotel_pro", "combo", "enterprise"];

const TIER_LABEL = {
  starter: "Starter",
  professional: "Professional",
  enterprise: "Enterprise",
  hotel_starter: "Hotel Starter",
  hotel_pro: "Hotel Pro",
  combo: "Combo",
};

const TIER_ACCENT = {
  starter: "#0F766E",
  professional: "#102A43",
  enterprise: "#7C3AED",
  hotel_starter: "#0F766E",
  hotel_pro: "#102A43",
  combo: "#DC2626",
};


export default function UpgradeModal({ open, onClose, mode = "checkout" }) {
  const [plans, setPlans] = useState(null);
  const [billingMe, setBillingMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      await Promise.resolve();
      if (cancelled) return;
      setLoading(true);
      setError("");
      try {
        const [plansRes, meRes] = await Promise.all([
          api.get("/billing/plans"),
          api.get("/billing/me"),
        ]);
        if (cancelled) return;
        setPlans(plansRes.data);
        setBillingMe(meRes.data);
      } catch (e) {
        if (!cancelled) {
          setError(
            e?.response?.data?.detail?.message ||
            e?.message ||
            "Impossibile caricare i piani."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Filter / reorder plans to show all commercial tiers visible to this tenant.
  const visiblePlans = useMemo(() => {
    const items = plans?.items || plans?.plans || [];
    if (items.length === 0) return [];
    // /api/billing/me usa `product_family` (restaurant / hotel / combo)
    // e i piani espongono `vertical` con la stessa shape.
    const bizType =
      billingMe?.product_family ||
      billingMe?.business_type ||
      billingMe?.tenant_business_type ||
      "restaurant";
    const filtered = items.filter((p) => {
      const vertical = (p.vertical || "").toLowerCase();
      // Enterprise è sempre visibile (cross-vertical upsell)
      if (vertical === "enterprise" || p.tier === "enterprise") return true;
      if (bizType === "hotel") return vertical === "hotel" || vertical === "combo";
      if (bizType === "combo") return vertical === "combo" || vertical === "hotel" || vertical === "restaurant";
      // restaurant default — show restaurant tiers + combo (upsell)
      return vertical === "restaurant" || vertical === "combo";
    });
    return filtered.sort((a, b) => {
      const ia = TIER_ORDER.indexOf(a.tier);
      const ib = TIER_ORDER.indexOf(b.tier);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
  }, [plans, billingMe]);

  const currentPlanId = billingMe?.plan_id || billingMe?.plan_code;
  const currentTier = billingMe?.plan_tier;
  const isTrialing =
    billingMe?.trial_active ||
    billingMe?.subscription_status === "trialing" ||
    !billingMe?.stripe_subscription_id;
  // Fix Stripe trial ripetuti — se il tenant NON è idoneo a un nuovo trial,
  // la CTA deve indicare pagamento immediato ("Attiva abbonamento") invece
  // di "14 giorni gratis".
  const trialEligible = billingMe?.trial_eligible !== false;

  const handleSelect = async (plan) => {
    if (submitting) return;
    setSubmitting(plan.id || plan.canonical_id);
    try {
      const useChangePlan = !isTrialing && billingMe?.stripe_subscription_id;
      if (useChangePlan) {
        const res = await api.post("/billing/subscription/change-plan", {
          plan_tier: plan.tier,
        });
        toast.success(`Piano aggiornato a ${TIER_LABEL[plan.tier] || plan.tier}`);
        onClose?.();
        if (res?.data?.redirect_url) {
          window.location.href = res.data.redirect_url;
        }
      } else {
        const res = await api.post("/billing/checkout", {
          plan_tier: plan.tier,
        });
        const url =
          res?.data?.checkout_url ||
          res?.data?.url ||
          res?.data?.session_url;
        if (!url) {
          throw new Error("Stripe non ha restituito un URL di checkout.");
        }
        window.location.href = url;
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.detail?.message ||
        e?.message ||
        "Errore durante l'attivazione del piano."
      );
      setSubmitting("");
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      data-testid="upgrade-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <p className="label-eyebrow flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-[#7C3AED]" />
              Attiva abbonamento
            </p>
            <h2 className="font-heading text-2xl font-bold text-[#0f172a] mt-1.5">
              Scegli il piano per il tuo workspace
            </h2>
            <p className="text-sm text-[#475569] mt-1.5">
              Pagamento sicuro su Stripe. Cambia o cancella il piano quando vuoi
              dal portale clienti.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#94A3B8] hover:text-[#0f172a]"
            data-testid="upgrade-modal-close"
            aria-label="Chiudi"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16" data-testid="upgrade-modal-loading">
            <Loader2 className="w-6 h-6 text-[#102A43] animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-16 text-sm text-[#B91C1C]" data-testid="upgrade-modal-error">
            {error}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="upgrade-modal-plans">
            {visiblePlans.map((plan) => (
              <PlanCard
                key={plan.canonical_id || plan.id || plan.tier}
                plan={plan}
                isCurrent={
                  plan.canonical_id === currentPlanId ||
                  plan.tier === currentTier
                }
                submitting={submitting === (plan.canonical_id || plan.id)}
                trialEligible={trialEligible}
                onSelect={() => handleSelect(plan)}
              />
            ))}
            {visiblePlans.length === 0 && (
              <div className="md:col-span-3 text-center py-12 text-sm text-[#94A3B8]" data-testid="upgrade-modal-empty">
                Nessun piano disponibile per il tuo settore.
              </div>
            )}
          </div>
        )}

        <p
          className="text-[11px] text-[#94A3B8] mt-5 text-center"
          data-testid="upgrade-modal-footer"
        >
          {trialEligible ? (
            <>
              Tutti i piani includono trial 14 giorni. La carta è richiesta
              solo all&apos;attivazione del piano. Pagamento gestito da Stripe.
            </>
          ) : (
            <>
              Hai già utilizzato la prova gratuita di 14 giorni.
              L&apos;abbonamento partirà subito con il primo addebito.
              Pagamento gestito da Stripe.
            </>
          )}
        </p>
      </div>
    </div>
  );
}


function PlanCard({ plan, isCurrent, submitting, trialEligible, onSelect }) {
  const accent = TIER_ACCENT[plan.tier] || "#102A43";
  const price = plan.price_eur_monthly ?? plan.price_monthly_eur ?? plan.price_eur;
  const features = plan.feature_bullets || plan.features || [];
  // Fix Stripe trial ripetuti — CTA dinamica in base a idoneità.
  const ctaLabel = isCurrent
    ? "Già attivo"
    : plan.tier === "enterprise"
    ? "Contattaci"
    : trialEligible
    ? "Attiva ora"
    : "Attiva abbonamento";
  const dueTodayLabel = trialEligible
    ? null
    : plan.tier === "enterprise"
    ? null
    : `Totale dovuto oggi: €${price}`;

  return (
    <div
      className={`surface-card p-5 flex flex-col gap-3 border ${
        isCurrent ? "border-[#0F766E]" : "border-transparent"
      }`}
      data-testid={`upgrade-plan-${plan.tier}`}
    >
      <div className="flex items-start justify-between">
        <p className="font-heading text-lg font-bold" style={{ color: accent }}>
          {plan.name || TIER_LABEL[plan.tier]}
        </p>
        {isCurrent && (
          <span className="text-[10px] font-semibold uppercase tracking-wider text-[#0F766E] bg-[#ECFDF5] px-2 py-0.5 rounded-full">
            Attuale
          </span>
        )}
      </div>
      <div>
        <p className="text-3xl font-extrabold text-[#0f172a] leading-none">
          {plan.tier === "enterprise" ? "Custom" : `€${price}`}
        </p>
        {plan.tier !== "enterprise" && (
          <p className="text-[11px] text-[#64748B] mt-1">al mese, +IVA</p>
        )}
      </div>
      <ul className="space-y-1.5 text-xs text-[#475569] flex-1">
        {(Array.isArray(features) ? features : []).slice(0, 6).map((f, i) => (
          <li key={i} className="flex items-start gap-1.5">
            <Check className="w-3.5 h-3.5 text-[#0F766E] shrink-0 mt-0.5" />
            <span>{typeof f === "string" ? f : f.label || f.name}</span>
          </li>
        ))}
      </ul>
      {dueTodayLabel && (
        <p
          className="text-[11px] font-semibold text-[#B45309] bg-[#FEF3C7] rounded-md px-2 py-1"
          data-testid={`upgrade-plan-due-today-${plan.tier}`}
        >
          {dueTodayLabel}
        </p>
      )}
      <button
        type="button"
        onClick={onSelect}
        disabled={submitting || isCurrent}
        className="mt-2 h-10 rounded-md text-sm font-semibold text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        style={{ background: accent }}
        data-testid={`upgrade-plan-select-${plan.tier}`}
      >
        {submitting ? (
          <Loader2 className="w-4 h-4 animate-spin mx-auto" />
        ) : (
          ctaLabel
        )}
      </button>
    </div>
  );
}
